import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Activity, Heart, Upload, LayoutDashboard, Settings,
  Trophy, Brain, Dumbbell, Zap, AlertTriangle, ClipboardList,
  Search
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useTranslation } from "@/hooks/useTranslation";
import { ROUTES } from '@/lib/routes';

interface CommandAction {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  category: string;
  action: () => void;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { translate: t } = useTranslation();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const exec = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const navCategory = t("cmd_category_navigation");
  const actCategory = t("cmd_category_actions");

  const commands: CommandAction[] = [
    // Navigation
    { id: "nav-dashboard", title: t("cmd_nav_dashboard"), icon: LayoutDashboard, category: navCategory, action: () => exec(ROUTES.DASHBOARD) },
    { id: "nav-players", title: t("cmd_nav_players"), icon: Users, category: navCategory, action: () => exec(ROUTES.PLAYERS) },
    { id: "nav-performance", title: t("cmd_nav_performance"), icon: Activity, category: navCategory, action: () => exec(ROUTES.PERFORMANCE) },
    { id: "nav-wellness", title: t("cmd_nav_wellness"), icon: Heart, category: navCategory, action: () => exec(ROUTES.WELLNESS) },
    { id: "nav-matches", title: t("cmd_nav_matches"), icon: Trophy, category: navCategory, action: () => exec(ROUTES.MATCHES) },
    { id: "nav-injuries", title: t("cmd_nav_injuries"), icon: AlertTriangle, category: navCategory, action: () => exec(ROUTES.INJURIES) },
    { id: "nav-actions", title: t("cmd_nav_actions"), icon: ClipboardList, category: navCategory, action: () => exec(ROUTES.ACTIONS) },
    { id: "nav-settings", title: t("cmd_nav_settings"), icon: Settings, category: navCategory, action: () => exec(ROUTES.SETTINGS) },
    { id: "nav-uploads", title: t("cmd_nav_uploads"), icon: Upload, category: navCategory, action: () => exec(ROUTES.UPLOADS) },
    // Actions
    { id: "act-gps", title: t("cmd_act_gps"), icon: Activity, category: actCategory, action: () => exec(`${ROUTES.PERFORMANCE}?tab=gps`) },
    { id: "act-strength", title: t("cmd_act_strength"), icon: Dumbbell, category: actCategory, action: () => exec(`${ROUTES.PERFORMANCE}?tab=strength`) },
    { id: "act-speed", title: t("cmd_act_speed"), icon: Zap, category: actCategory, action: () => exec(`${ROUTES.PERFORMANCE}?tab=speed`) },
    { id: "act-cognitive", title: t("cmd_act_cognitive"), icon: Brain, category: actCategory, action: () => exec(`${ROUTES.PERFORMANCE}?tab=cognitive`) },
    { id: "act-upload", title: t("cmd_act_upload"), icon: Upload, category: actCategory, action: () => exec(ROUTES.UPLOADS) },
  ];

  const categories = [actCategory, navCategory];

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder={t("cmd_placeholder")} />
      <CommandList>
        <CommandEmpty>{t("cmd_no_results")}</CommandEmpty>
        {categories.map(category => (
          <CommandGroup key={category} heading={category}>
            {commands
              .filter(cmd => cmd.category === category)
              .map(cmd => (
                <CommandItem key={cmd.id} onSelect={cmd.action} className="gap-2">
                  <cmd.icon className="h-4 w-4 text-muted-foreground" />
                  <span>{cmd.title}</span>
                </CommandItem>
              ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
